import { useState, type ReactNode } from 'react';
import { Alert, Box, Button, IconButton, Link as MuiLink, Stack, Switch, Tooltip, Typography } from '@mui/material';
import { CheckCircleIcon, CopyIcon, RocketLaunchIcon } from '@phosphor-icons/react';

import { useSite } from '@/hooks/use-site';
import { usePublicSiteUrl } from '@/hooks/use-public-site-url';
import type { Site } from '@/types/database';

interface LaunchChecklistProps {
  site: Site;
  onDone: () => void;
}

// Spec section 4, Step 8: "Post-publish card with five actions." Two are
// real in-app actions (turning on the Calendar module, copying the site
// URL); the rest point to where the action actually happens today —
// meeting schedule and admin invites don't have an editing surface in
// this repo (by design, see docs/site-builder-overhaul.md's Phase B), and
// there's no photo/gallery module yet (Phase F), so those are described
// honestly rather than wired to something that doesn't exist.
export function LaunchChecklist({ site, onDone }: LaunchChecklistProps) {
  const { save } = useSite();
  const { url: publicUrl } = usePublicSiteUrl();
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!publicUrl) return;
    void navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Stack spacing={3}>
      <Stack spacing={1} sx={{ alignItems: 'center', textAlign: 'center' }}>
        <RocketLaunchIcon size={40} weight="fill" color="var(--mui-palette-primary-main)" />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Your site is live!
        </Typography>
        {publicUrl ? (
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
            <MuiLink href={publicUrl} target="_blank" rel="noopener noreferrer">
              {publicUrl.replace(/^https?:\/\//, '')}
            </MuiLink>
            <Tooltip title={copied ? 'Copied!' : 'Copy link'}>
              <IconButton size="small" onClick={handleCopy} aria-label="Copy site link">
                <CopyIcon size={16} />
              </IconButton>
            </Tooltip>
          </Stack>
        ) : null}
      </Stack>

      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
        A few things worth doing next
      </Typography>

      <ChecklistItem title="Add your meeting time">
        Meeting day, time, and location are managed in Scouting Ops and appear on your Unit Identity Card automatically once set.
      </ChecklistItem>

      <ChecklistItem title="Add your next three events">
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
          <Switch
            checked={site.calendar_visible_on_site}
            onChange={(event) => void save({ calendar_visible_on_site: event.target.checked })}
          />
          <Typography variant="body2" color="text.secondary">
            Show real events from Scouting Ops Calendar on your site
          </Typography>
        </Stack>
      </ChecklistItem>

      <ChecklistItem title="Add one photo">
        Add an Image block to your Home page in Studio — a real photo makes a much stronger first impression than a stock one.
      </ChecklistItem>

      <ChecklistItem title="Update your BeAScout pin">
        Copy your site link above and paste it into your unit&apos;s web address field in my.Scouting Organization Manager, so
        prospective families searching BeAScout can find you.
      </ChecklistItem>

      <ChecklistItem title="Invite a second admin">
        A site with only one admin can be orphaned if that person loses access. Add a co-admin from Scouting Ops Hub.
      </ChecklistItem>

      <Alert severity="info">You can revisit any of this later — nothing here is a one-time choice.</Alert>

      <Button variant="contained" size="large" onClick={onDone}>
        Go to my site
      </Button>
    </Stack>
  );
}

function ChecklistItem({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
      <CheckCircleIcon size={20} style={{ marginTop: 2, flexShrink: 0 }} color="var(--mui-palette-text-secondary)" />
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontWeight: 700, mb: 0.5 }}>{title}</Typography>
        {children}
      </Box>
    </Stack>
  );
}
